/**
 * Authentication Context — thin wrapper around DryContext auth.
 * Maintains backward compatibility with useAuth() hook.
 * All auth state and actions now live in DryProvider (DryAuth lifecycle).
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { User, Tenant, LoginRequest, RegisterRequest } from '../types';
import { useDry } from '../dry/DryContext';

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  labels: Record<string, { singular: string; plural: string }>;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // All auth state and actions now come from DryContext
  const dry = useDry();

  const value: AuthContextType = {
    user: dry.user,
    tenant: dry.tenant,
    labels: dry.labels,
    isAuthenticated: dry.isAuthenticated,
    isLoading: dry.isAuthLoading,
    login: dry.login,
    register: dry.register,
    logout: dry.logout,
    refreshUser: dry.refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Re-export selector hooks that read from DryContext
export const useUser = () => {
  const { user } = useDry();
  return user;
};

export const useTenant = () => {
  const { tenant } = useDry();
  return tenant;
};

export const useIsAuthenticated = () => {
  const { isAuthenticated } = useDry();
  return isAuthenticated;
};

export const useIsAuthLoading = () => {
  const { isAuthLoading } = useDry();
  return isAuthLoading;
};

export const useAuthActions = () => {
  const { login, register, logout, refreshUser } = useDry();
  return { login, register, logout, refreshUser };
};
